import * as React from "react";
import { TreeListBooleanEditor, TreeList, mapTree, treeToFlat } from '@progress/kendo-react-treelist'
import { AccentButton } from "../controls/AccentButton";
import { accentUtils, format, from, t } from "../services/HelperService";
import { AccentDialog } from "./AccentDialog";
import { AccentSpinner } from "../controls/AccentSpinner";
import { usePosition } from "../controls/AccentDisplay";
import { update } from "../services/DataService";
import { Alert, Tooltip } from "reactstrap";
import AccentIcons from "../icons/Icons";





const processTree = (dataTree, childrenField, processItem) => {    
    return mapTree(dataTree, childrenField, (item) =>processItem(item));
};

export const MoveToSupplyDlg = props => {

    const [data, setData] = React.useState(null);
    const [expanded, setExpanded] = React.useState({});
    const [selected, setSelected] = React.useState({});
    const [hasAccess, setHasAccess] = React.useState(true);

    const pos = usePosition("moveToSupplyTree");

    const onExpandChanged = React.useCallback(e => {

        setExpanded(existing => {
            return { ...existing, [e.dataItem.ID]: !e.value };
        });

    }, [setExpanded])

    const onItemChanged = React.useCallback(e => {


        const items = treeToFlat([e.dataItem], "ID", "Children");

        const newChecked = from(items).select(i => i.ID).toArray().reduce((a, v) => ({ ...a, [v]: e.value }), {});

        setSelected(existing => {
            return { ...existing, ...newChecked };
        });
    }, [setSelected]);


    const onMoveToSupply = React.useCallback(e => {

        const allItems = treeToFlat(data, "ID", "Children");

        props.onClose({
            canceled: false,
            items: from(allItems)
                .where(i => selected[i.ID] && !accentUtils.isEmpty(i.LineID))
                .groupBy(i => i.JobID)
                .select(g => {
                    return {
                        ID: g.key(),
                        LineIDs: g.select(x => x.LineID).toArray()
                    };
                }).toArray()
        });

    },[data, selected, props]);



    React.useEffect(() => {

        update("GetMoveToSupplyWizardModel", { Jobs: props.jobIDs }).then(model => {

            if (!model.UserHasAccess) {

                setHasAccess(false);

            } else {
                setHasAccess(true);

                const allExpanded = {};
                const defaultSelection = {};
                const r = model.Tree;



                setData(processTree([r], "Children", item => {

                    allExpanded[item.ID] = true;

                    const itemJob = from(props.jobIDs).firstOrDefault(x => x.ID === item.JobID);
                    const hasLineFilter = (itemJob?.LineIDs ?? []).length > 0;
                    const lineIDFilterSelect = from((itemJob?.LineIDs ?? [])).any(id => id === Number(item.ID));


                    defaultSelection[item.ID] = !hasLineFilter || lineIDFilterSelect;

                    return { ...item, expanded: true, editing: true, Stage: t(item.Stage), Dispatch: t(item.Dispatch) };
                }));



                setSelected(defaultSelection);
                setExpanded(allExpanded);


            }
        })
    }, [props.jobIDs]);


    const hasSelection = from(Object.values(selected)).any(s => s === true);

    let hasInvalidSelection = !hasSelection;
    let qty = 0;
    let creditLimitBlocked = false;
    const treeData = processTree(data ?? [], "Children", item => {

        const itemSelected = selected[item.ID] ?? false;

        const isLineItem = (item.Children?.length ?? 0) === 0;

        if (itemSelected && !(item.Validation?.CanMove) && isLineItem)
            hasInvalidSelection = true;

        if (itemSelected && isLineItem && item.Validation?.CreditLimitExceeded && !item.Validation?.CreditLimitCanOverride)
            creditLimitBlocked = true;

        if (isLineItem && itemSelected) {
            qty += (item.Qty ?? 0);
        }

        const validation = item.Validation || {};
        const augmentedValidation = validation.CreditLimitExceeded
            ? { ...validation, CreditLimitBlocked: !validation.CreditLimitCanOverride }
            : validation;

        return { ...item, expanded: expanded[item.ID] ?? false, selected: itemSelected, Validation: augmentedValidation };
    });

    const height = pos.toScreenHeight - 50;

    return <AccentDialog
        fullscreen
        headerContent={t("application_strings.application.dialogs.moveToSupply.title")}
        onClose={props.onClose}
        actionsContent={[<AccentButton key="1" disabled={hasInvalidSelection || creditLimitBlocked} tagName="save" onClick={onMoveToSupply}>{t("application_strings.application.buttons.moveToSupply")}</AccentButton>]}
    >
        {!hasAccess && <Alert color="danger">{t("application_strings.application.dialogs.moveToSupply.noAccess")}</Alert>}
        {hasAccess && accentUtils.isNull(data) && <AccentSpinner />}
        {hasAccess && !accentUtils.isNull(data) &&

            <div id="moveToSupplyTree">
                <TreeList                    
                    data={treeData}
                    style={{
                        height: `${height}px`,
                        overflow: "auto",
                    }}
                    editField="editing"
                    expandField="expanded"
                    subItemsField="Children"
                    validationField="validation"
                    columns={columns}
                    onExpandChange={onExpandChanged}
                    onItemChange={onItemChanged} />
                <div className="float-right"><label>{t("application_strings.application.dialogs.moveToSupply.selectedQty")}</label>:<span style={{ laddingLeft: "20px" }}>{qty}</span></div>
            </div>
        }
    </AccentDialog>

};

function getValidationMessage(validation) {
    if (!validation) return "";

    if (validation.CreditLimitExceeded) {
        const outstandingTotal = validation.AmountOutstanding != null ? format.formatCurrency(validation.AmountOutstanding) : "";
        const creditLimit = validation.CreditLimit != null ? format.formatCurrency(validation.CreditLimit) : "";
        return validation.CreditLimitBlocked
            ? t("application_strings.application.dialogs.moveToSupplyCreditLimitExceeded.msg", { outstandingTotal, creditLimit })
            : t("application_strings.application.dialogs.moveToSupplyCreditLimitExceeded.msgCanProceed", { outstandingTotal, creditLimit });
    }
    if (validation.CanMove) {
        return "";
    } else if (validation.InEdit) {
        return t("application_strings.application.dialogs.moveToSupply.inEdit");
    } else if (validation.ValidationErrors) {
        return t("application_strings.application.dialogs.moveToSupply.validationErrors");
    } else if (validation.RequireCM) {
        return t("application_strings.application.dialogs.moveToSupply.requireCM");
    } else if (validation.InSupply) {
        return t("application_strings.application.dialogs.moveToSupply.inSupply");
    } else if (validation.Dispatched) {
        return t("application_strings.application.dialogs.moveToSupply.dispatched");
    } else if (validation.Dispatch) {
        return t("application_strings.application.dialogs.moveToSupply.dispatch");
    } else if (validation.Restock) {
        return t("application_strings.application.dialogs.moveToSupply.restock");
    } else if (validation.OnHold) {
        return t("application_strings.application.dialogs.moveToSupply.onHold");
    } else if (validation.CompanyOnHold) {
        return validation.CompanyOnHoldCanOverride
            ? t("application_strings.application.dialogs.moveToSupply.companyOnHoldWarn")
            : t("application_strings.application.dialogs.moveToSupply.companyOnHoldBlocked");
    } else if (validation.CustomValidation) {
        return getCustomValidationMessages(validation).join("\n") || t("Custom validation failed");
    }

    return "";
}

function getCustomValidationMessages(validation) {
    return (validation?.CustomValidationMessages ?? []).filter(m => !accentUtils.isEmpty(m));
}

const ValidtionCell = (props) => {
    const { dataItem } = props;
    const field = props.field || "";
    const validation = dataItem[field];
    const [tooltipOpen, setTooltipOpen] = React.useState(false);

    const isLine = !accentUtils.isEmpty(dataItem.LineID);
    const hasChildren = (dataItem?.Children?.length ?? 0) > 0;
    const isExpanded = dataItem.expanded;

    let value = "";
    let warningValue = "";
    let tooltipContent = null;
    let isValid = false; 
    let isWarning = false;
    let warningId = null;
    let creditLimitTooltipContent = null;

    if (isLine) {
        isValid = validation?.CanMove;
        value = getValidationMessage(validation);

        if (validation?.CreditLimitExceeded) {
            creditLimitTooltipContent = value;
            value = t("application_strings.application.dialogs.moveToSupplyCreditLimitExceeded.msgShort");
            isValid = !validation.CreditLimitBlocked;
            isWarning = !validation.CreditLimitBlocked;
        }
        // If there is no other blocking reason, surface company hold status.
        else if (accentUtils.isEmpty(value) && validation?.CompanyOnHold) {
            isWarning = validation.CompanyOnHoldCanOverride;
            value = validation.CompanyOnHoldCanOverride
                ? t("application_strings.application.dialogs.moveToSupply.companyOnHoldWarn")
                : t("application_strings.application.dialogs.moveToSupply.companyOnHoldBlocked");
            isValid = validation.CompanyOnHoldCanOverride;
        }

        if (validation.HasSupplyNotes) {
            warningId = `warning-line-${String(dataItem.ID).replace(/\$/g, '-')}`;
            const lineNotes = dataItem.SupplyNotes;
            const jobNotes = dataItem.JobSupplyNotes;
            warningValue = t("application_strings.application.dialogs.moveToSupply.hasSupplyNotesWarning");
            
            let notesText = "";
            if (!accentUtils.isEmpty(lineNotes)) {
                notesText += `Line: ${lineNotes}`;
            }
            if (!accentUtils.isEmpty(jobNotes)) {
                if (notesText) notesText += "\n";
                notesText += `Job: ${jobNotes}`;
            }
             tooltipContent = (
                 <div>
                     <strong>{warningValue}</strong>
                     {!accentUtils.isEmpty(notesText) && <pre style={{ marginTop: '5px', marginBottom: '0' }}>{notesText}</pre>}
                 </div>
            );

        }
        if (isValid && accentUtils.isEmpty(value)) {
             value = t("application_strings.application.dialogs.moveToSupply.ready");
        }

    } else if (hasChildren && !isExpanded) {
        const items = treeToFlat([dataItem], "ID", "Children");
        const childLineItems = from(items).where(i => i.ID !== dataItem.ID && (i.Children?.length ?? 0) === 0);

        const hasInvalidChild = childLineItems.any(i => !i.Validation?.CanMove || i.Validation?.CreditLimitBlocked);
        const hasCompanyHoldWarningChild = childLineItems.any(i => i.Validation?.CompanyOnHold && i.Validation?.CompanyOnHoldCanOverride);
        const hasCreditLimitWarningChild = childLineItems.any(i => i.Validation?.CreditLimitExceeded && !i.Validation?.CreditLimitBlocked);

        if (hasInvalidChild) {
            value = t("application_strings.application.dialogs.moveToSupply.hasError");
            isValid = false;
            isWarning = false;
        } else if (hasCompanyHoldWarningChild || hasCreditLimitWarningChild) {
            if (hasCreditLimitWarningChild) {
                const firstWithCreditLimit = childLineItems.firstOrDefault(i => i.Validation?.CreditLimitExceeded);
                const outstandingTotal = firstWithCreditLimit?.Validation?.AmountOutstanding != null ? format.formatCurrency(firstWithCreditLimit.Validation.AmountOutstanding) : "";
                const creditLimitVal = firstWithCreditLimit?.Validation?.CreditLimit != null ? format.formatCurrency(firstWithCreditLimit.Validation.CreditLimit) : "";
                creditLimitTooltipContent = t("application_strings.application.dialogs.moveToSupplyCreditLimitExceeded.msgCanProceed", { outstandingTotal, creditLimit: creditLimitVal });
                value = t("application_strings.application.dialogs.moveToSupplyCreditLimitExceeded.msgShort");
            } else {
                value = t("application_strings.application.dialogs.moveToSupply.companyOnHoldWarn");
            }
            isValid = true;
            isWarning = true;
        } else {
            value = t("application_strings.application.dialogs.moveToSupply.ready");
            isValid = true; 
        }
        
        const hasWarningChild = childLineItems.any(i => i.Validation.HasSupplyNotes);
        if (hasWarningChild) {
             warningId = `warning-parent-${String(dataItem.ID).replace(/\$/g, '-')}`;
             warningValue = t("application_strings.application.dialogs.moveToSupply.hasSupplyNotesWarning"); 
             tooltipContent = warningValue;
        }

    } else {
        return <td></td>;
    }

    const toggleTooltip = () => setTooltipOpen(!tooltipOpen);

    const textColor = isWarning ? "orange" : (isValid ? "green" : "red");
    const customValidationMessages = validation?.CustomValidation && !creditLimitTooltipContent
        ? getCustomValidationMessages(validation)
        : [];
    const customValidationValue = customValidationMessages.join("\n") || t("Custom validation failed");
    const valueIsCustomValidationOnly = validation?.CustomValidation && value === customValidationValue;
    const validationMessages = [
        ...(!accentUtils.isEmpty(value) && !valueIsCustomValidationOnly ? [value] : []),
        ...(customValidationMessages.length > 0 ? customValidationMessages : (valueIsCustomValidationOnly ? [customValidationValue] : []))
    ];

    return (
        <td>
            {creditLimitTooltipContent ? (
                <div className="d-flex">
                    <div><AccentIcons.Info style={{ color: textColor }} toolTipText={creditLimitTooltipContent} /></div>
                    <div style={{ color: textColor, marginLeft: "5px" }}>{value}</div>
                </div>
            ) : validationMessages.length > 1 ? (
                <div style={{ color: textColor, display: "flex", flexDirection: "column", gap: "2px", lineHeight: "1.35" }}>
                    {validationMessages.map((message, ix) => (
                        <div key={`${dataItem.ID}-validation-${ix}`} style={{ whiteSpace: "pre-line" }}>
                            {message}
                        </div>
                    ))}
                </div>
            ) : (
                <span style={{ color: textColor }}>{value}</span>
            )}
            {!accentUtils.isEmpty(warningValue) && warningId && (
                <>
                    <span id={warningId} style={{ color: "orange", marginLeft: "5px", fontWeight: "bold" }}>
                       <AccentIcons.Warning top={7} />
                    </span>
                    <Tooltip
                        placement="top"
                        isOpen={tooltipOpen}
                        target={warningId}
                        toggle={toggleTooltip}
                        autohide={false}
                    >
                        {tooltipContent}
                    </Tooltip>
                </>
            )}
        </td>
    );
};



const columns = [
    {
        field: "selected",
        title: " ",
        expandable: true,
        editCell: TreeListBooleanEditor,
        locked: true,
        width: 30,
    },
    {
        field: "Item",
        title: "Item",
        width: 250,
        expandable: true,

    },
    {
        field: "Validation",
        title: "Validation",
        width: 250,
        expandable: false,
        cell: ValidtionCell
    },
    {
        field: "Qty",
        title: "Qty",
        width: 70,
        expandable: false,
    },
    {
        field: "Stage",
        title: "Stage",
        width: 250,
        expandable: false,
    },
    {
        field: "Location",
        title: "Location",
        width: 250,
        expandable: false,
    },
    {
        field: "Dispatch",
        title: "Dispatch",
        width: 250,
        expandable: false,
    },
    {
        field: "DisplayOption1",
        title: "Option 1",
        width: 250,
        expandable: false,
    },
    {
        field: "DisplayOption2",
        title: "Option 2",
        width: 250,
        expandable: false,
    },
    {
        field: "DisplayOption3",
        title: "Option 3",
        width: 250,
        expandable: false,
    }

];